<template>
  <div class="min-h-screen bg-gray-50 p-6 pt-24 lg:pt-8" style="direction: rtl; text-align: right;">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-gray-900">إدارة صلاحيات المستخدمين</h1>
        <p class="mt-2 text-sm text-gray-600">تعيين مجموعات الصلاحيات للمستخدمين</p>
      </div>

      <!-- Search -->
      <div class="mb-6">
        <input
          v-model="searchTerm"
          type="text"
          placeholder="البحث عن مستخدم..."
          class="w-full max-w-md px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-customHafadhColor"
        />
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-customHafadhColor"></div>
        <p class="mt-4 text-gray-600">جاري تحميل البيانات...</p>
      </div>

      <!-- Error State -->
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <p class="text-red-800">{{ error }}</p>
      </div>

      <!-- Users Table -->
      <div v-if="!loading && filteredUsers.length > 0" class="bg-white rounded-lg shadow overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-customHafadhColor">
            <tr>
              <th class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">الاسم</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">البريد الإلكتروني</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">الدور</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">مجموعات الصلاحيات</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">الإجراءات</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="user in filteredUsers" :key="user.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ user.full_name }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ user.email }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ user.role_name || '-' }}</td>
              <td class="px-6 py-4 text-sm text-gray-500">
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="group in user.permission_groups"
                    :key="group.id"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {{ group.name }}
                  </span>
                  <span v-if="user.permission_groups.length === 0" class="text-gray-400">لا توجد مجموعات</span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button
                  @click="openAssignModal(user)"
                  class="text-customHafadhColor hover:text-customHafadhColorHover"
                >
                  إدارة المجموعات
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && filteredUsers.length === 0" class="bg-white rounded-lg shadow p-12 text-center">
        <p class="text-gray-500">لا توجد مستخدمين</p>
      </div>

      <!-- Assign Groups Modal -->
      <div v-if="showAssignModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="closeAssignModal">
        <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeAssignModal"></div>
          <div class="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                إدارة مجموعات الصلاحيات: {{ selectedUser?.full_name }}
              </h3>
              <div v-if="groupsLoading" class="text-center py-8">
                <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-customHafadhColor"></div>
              </div>
              <div v-else class="space-y-3">
                <div
                  v-for="group in availableGroups"
                  :key="group.id"
                  class="flex items-center justify-between p-3 border border-gray-200 rounded-md"
                >
                  <div>
                    <p class="text-sm font-medium text-gray-900">{{ group.name }}</p>
                    <p v-if="group.description" class="text-xs text-gray-500 mt-1">{{ group.description }}</p>
                  </div>
                  <input
                    type="checkbox"
                    :checked="isGroupAssigned(group.id)"
                    @change="toggleGroup(group.id, $event.target.checked)"
                    class="h-4 w-4 text-customHafadhColor focus:ring-customHafadhColor border-gray-300 rounded"
                  />
                </div>
                <p v-if="availableGroups.length === 0" class="text-sm text-gray-500 text-center py-4">
                  لا توجد مجموعات صلاحيات متاحة
                </p>
              </div>
            </div>
            <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                @click="saveUserGroups"
                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-customHafadhColor text-base font-medium text-white hover:bg-customHafadhColorHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-customHafadhColor sm:ml-3 sm:w-auto sm:text-sm"
              >
                حفظ
              </button>
              <button
                @click="closeAssignModal"
                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import url from '@/url';

export default {
  name: 'UserPermissionsManagement',
  data() {
    return {
      users: [],
      availableGroups: [],
      loading: false,
      groupsLoading: false,
      error: null,
      searchTerm: '',
      showAssignModal: false,
      selectedUser: null,
      selectedGroups: [], // Array of group IDs
    };
  },
  computed: {
    filteredUsers() {
      if (!this.searchTerm) {
        return this.users;
      }
      const term = this.searchTerm.toLowerCase();
      return this.users.filter(user =>
        user.full_name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term)
      );
    },
  },
  mounted() {
    this.loadUsers();
    this.loadGroups();
  },
  methods: {
    async loadUsers() {
      this.loading = true;
      this.error = null;
      try {
        const token = localStorage.getItem('token');
        // Load admins and general managers
        const [adminsResponse, teachersResponse] = await Promise.all([
          axios.get(
            process.env.VUE_APP_SERVICE_URL + url.get_all_admins,
            {
              headers: {
                'X-Authorization': token,
              },
            }
          ),
          axios.get(
            process.env.VUE_APP_SERVICE_URL + url.get_all_teachers,
            {
              headers: {
                'X-Authorization': token,
              },
            }
          ),
        ]);

        const admins = adminsResponse.data?.result?.data || adminsResponse.data?.data || [];
        const teachers = teachersResponse.data?.result?.data || teachersResponse.data?.data || [];

        // Combine and format users
        this.users = [...admins, ...teachers].map(user => ({
          id: user.id,
          full_name: user.full_name || user.fullName,
          email: user.email,
          role_name: user.role?.role_name || user.role_name || '-',
          permission_groups: [], // Will be loaded separately
        }));

        // Load permission groups for each user
        await Promise.all(this.users.map(user => this.loadUserGroups(user)));
      } catch (error) {
        console.error('Error loading users:', error);
        this.error = error.response?.data?.result?.mesAr || 'حدث خطأ أثناء تحميل البيانات';
      } finally {
        this.loading = false;
      }
    },
    async loadUserGroups(user) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.permissions_user_groups + user.id + '/groups',
          {
            headers: {
              'X-Authorization': token,
            },
          }
        );

        // Update user's permission groups
        if (response.data && response.data.result && response.data.result.data) {
          const groups = response.data.result.data.groups || [];
          const userIndex = this.users.findIndex(u => u.id === user.id);
          if (userIndex !== -1) {
            // Vue 3: Direct assignment works with reactivity
            this.users[userIndex].permission_groups = groups;
          }
        }
      } catch (error) {
        console.error('Error loading user groups:', error);
        // Set empty array on error
        const userIndex = this.users.findIndex(u => u.id === user.id);
        if (userIndex !== -1) {
          // Vue 3: Direct assignment works with reactivity
          this.users[userIndex].permission_groups = [];
        }
      }
    },
    async loadGroups() {
      this.groupsLoading = true;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.permission_groups_list,
          {
            headers: {
              'X-Authorization': token,
            },
          }
        );
        
        if (response.data && response.data.result && response.data.result.data) {
          this.availableGroups = response.data.result.data.groups || [];
        } else if (response.data && response.data.data) {
          this.availableGroups = response.data.data.groups || [];
        }
      } catch (error) {
        console.error('Error loading groups:', error);
      } finally {
        this.groupsLoading = false;
      }
    },
    openAssignModal(user) {
      this.selectedUser = user;
      this.selectedGroups = user.permission_groups?.map(g => g.id) || [];
      this.showAssignModal = true;
    },
    closeAssignModal() {
      this.showAssignModal = false;
      this.selectedUser = null;
      this.selectedGroups = [];
    },
    isGroupAssigned(groupId) {
      return this.selectedGroups.includes(groupId);
    },
    toggleGroup(groupId, checked) {
      if (checked) {
        if (!this.selectedGroups.includes(groupId)) {
          this.selectedGroups.push(groupId);
        }
      } else {
        this.selectedGroups = this.selectedGroups.filter(id => id !== groupId);
      }
    },
    async saveUserGroups() {
      if (!this.selectedUser) return;

      try {
        const token = localStorage.getItem('token');
        
        // Get current user groups
        const currentGroups = this.selectedUser.permission_groups?.map(g => g.id) || [];
        
        // Find groups to add and remove
        const groupsToAdd = this.selectedGroups.filter(id => !currentGroups.includes(id));
        const groupsToRemove = currentGroups.filter(id => !this.selectedGroups.includes(id));

        // Add new groups
        for (const groupId of groupsToAdd) {
          await axios.post(
            process.env.VUE_APP_SERVICE_URL + url.permission_groups_assign_user + groupId + '/assign-user',
            { user_id: this.selectedUser.id },
            {
              headers: {
                'X-Authorization': token,
              },
            }
          );
        }

        // Remove groups
        for (const groupId of groupsToRemove) {
          await axios.delete(
            process.env.VUE_APP_SERVICE_URL + url.permission_groups_remove_user + groupId + '/users/' + this.selectedUser.id,
            {
              headers: {
                'X-Authorization': token,
              },
            }
          );
        }

        this.closeAssignModal();
        this.loadUsers();
      } catch (error) {
        console.error('Error saving user groups:', error);
        alert(error.response?.data?.result?.mesAr || 'حدث خطأ أثناء الحفظ');
      }
    },
  },
};
</script>

